import dayjs, { type Dayjs } from 'dayjs';
import type { ReferralFormItem } from '@/modules/zy-crm/components/referral-form-section';
import type { ResourceAreaValue } from '@/modules/zy-crm/components/resource-area-selector';
import { toApiDateTime } from '@/modules/zy-crm/utils/display';
import type {
  CreateCustomerDto,
  CustomerItem,
  UpdateCustomerDto,
} from '@/services/bff/types/customer';

export type CustomerFormValues = {
  name?: string;
  fullName?: string;
  unifiedSocialCreditCode?: string;
  legalRepresentative?: string;
  registeredCapital?: string;
  establishmentDate?: Dayjs;
  followUpStatus: number | string;
  contactLastTime?: Dayjs;
  contactLastContent?: string;
  contactNextTime?: Dayjs;
  ownerUserUuid?: string;
  ownerTime: Dayjs;
  lockStatus: boolean | string | number;
  dealStatus: boolean | string | number;
  mobile?: string;
  telephone?: string;
  qq?: string;
  wechat?: string;
  email?: string;
  website?: string;
  qichachaUrl?: string | null;
  tianyanchaUrl?: string | null;
  areas?: ResourceAreaValue[];
  industryUuid?: string;
  csrcIndustryUuid?: string;
  customerLevel?: string | number;
  sourceChannel?: string | number;
  contactUuids?: string[];
  tagUuids?: string[];
  remark?: string;
  referrals?: ReferralFormItem[];
};

const toDateValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const toBooleanValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
  }
  return Boolean(value);
};

const numericSelectFields = new Set<keyof CustomerFormValues>([
  'followUpStatus',
  'customerLevel',
]);

const toOptionalNumberValue = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return value;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
};

export const sanitizeMemberUuid = (value?: string | null) => {
  const text = String(value || '').trim();
  if (!text) {
    return undefined;
  }
  return text;
};

export const toCustomerFormValues = (
  record: CustomerItem | null,
  currentMemberUuid: string | null,
): Partial<CustomerFormValues> => {
  if (record) {
    return {
      name: record.name,
      fullName: record.fullName,
      unifiedSocialCreditCode: record.unifiedSocialCreditCode,
      legalRepresentative: record.legalRepresentative,
      registeredCapital: record.registeredCapital,
      establishmentDate: toDateValue(record.establishmentDate),
      followUpStatus: record.followUpStatus ?? 0,
      contactLastTime: toDateValue(record.contactLastTime),
      contactLastContent: record.contactLastContent,
      contactNextTime: toDateValue(record.contactNextTime),
      ownerUserUuid: record.ownerUserUuid,
      ownerTime: toDateValue(record.ownerTime),
      lockStatus: String(record.lockStatus ?? false),
      dealStatus: String(record.dealStatus ?? false),
      mobile: record.mobile,
      telephone: record.telephone,
      qq: record.qq,
      wechat: record.wechat,
      email: record.email,
      website: record.website,
      qichachaUrl: record.qichachaUrl,
      tianyanchaUrl: record.tianyanchaUrl,
      areas: record.areas,
      industryUuid: record.industryUuid,
      csrcIndustryUuid: record.csrcIndustryUuid,
      customerLevel: record.customerLevel,
      sourceChannel: record.sourceChannel,
      tagUuids: (record.tags || []).map((tag) => tag.uuid),
      remark: record.remark,
    };
  }
  return {
    followUpStatus: 0,
    ownerUserUuid: currentMemberUuid || undefined,
    ownerTime: dayjs(),
    lockStatus: 'false',
    dealStatus: 'false',
  };
};

export const normalizeCustomerPayload = (
  values: CustomerFormValues,
): Partial<CreateCustomerDto | UpdateCustomerDto> => {
  const payload: Record<string, unknown> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key === 'qichachaUrl' || key === 'tianyanchaUrl') {
      payload[key] =
        typeof value === 'string' && value.trim() ? value.trim() : null;
      return;
    }
    if (key === 'referrals' || key === 'contactUuids' || key === 'tagUuids') {
      return;
    }
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value === '')
    ) {
      return;
    }
    if (key === 'ownerUserUuid') {
      const normalized = sanitizeMemberUuid(String(value));
      if (normalized) {
        payload[key] = normalized;
      }
      return;
    }
    if (key === 'lockStatus' || key === 'dealStatus') {
      payload[key] = toBooleanValue(value);
      return;
    }
    if (numericSelectFields.has(key as keyof CustomerFormValues)) {
      payload[key] = toOptionalNumberValue(value);
      return;
    }
    if (dayjs.isDayjs(value)) {
      payload[key] = toApiDateTime(value);
      return;
    }
    payload[key] = value;
  });
  return payload;
};
